import React from 'react';
import { getImageProps } from 'next/image';
import { preload } from 'react-dom';
import { cn } from '@/lib/utils';

const MOBILE_MEDIA = '(max-width: 767px)';
const TABLET_MEDIA = '(min-width: 768px) and (max-width: 1023px)';
const DESKTOP_MEDIA = '(min-width: 1024px)';

// One crop per viewport, so a device downloads the file made for its screen.
// Takes static imports, or remote URLs when `fill` is set.
//
// Static imports are served as-is from /_next/static/media, so the files must already be sized
// (3x phone, 2x tablet/desktop) and compressed. They skip the optimiser, which processes each size
// on first request and loses its cache on every deploy, making images slow after each release.
// Remote URLs (CMS uploads) still go through the optimiser, as their size is unknown.
//
// fill: the image covers its parent, which must have its own width and height.
// fill={false}: the image sets the height; each crop carries its own width and
// height so the browser reserves the right space before it loads.
// sizes: how wide the image is shown, so smaller screens get a smaller copy of remote URLs.
// priority: above-the-fold images load eagerly at high priority and are preloaded from <head>
// (only the crop matching the viewport); everything else loads lazily.
// pictureClassName: layout classes for the <picture> wrapper itself (visibility, order, overflow).
export const ResponsivePicture = ({
  mobile, tablet, desktop, alt, fill = false, sizes = '100vw', className, pictureClassName, priority = false,
}) => {
  const loading = priority ? { loading: 'eager', fetchPriority: 'high' } : { loading: 'lazy' };
  const imageProps = (src) =>
    getImageProps({ src, alt, fill, sizes, unoptimized: typeof src !== 'string', ...loading }).props;
  const mob = imageProps(mobile);
  const tab = imageProps(tablet);
  const desk = imageProps(desktop);

  if (priority) {
    [
      [mob, MOBILE_MEDIA],
      [tab, TABLET_MEDIA],
      [desk, DESKTOP_MEDIA],
    ].forEach(([{ src, srcSet }, media]) =>
      preload(src, { as: 'image', fetchPriority: 'high', imageSrcSet: srcSet, imageSizes: sizes, media })
    );
  }

  return (
    <picture className={pictureClassName}>
      <source media={MOBILE_MEDIA} srcSet={mob.srcSet ?? mob.src} sizes={sizes} width={mob.width} height={mob.height} />
      <source media={TABLET_MEDIA} srcSet={tab.srcSet ?? tab.src} sizes={sizes} width={tab.width} height={tab.height} />
      <img {...desk} alt={alt} className={cn(fill ? 'object-cover' : 'w-full h-auto', className)} />
    </picture>
  );
};
